/**
 * Custom Carousel Component
 * 
 * This script initializes and manages the custom carousel functionality
 * for the custom-carousel.liquid section.
 */

class CustomCarousel extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('.custom-carousel__slider');
    this.slides = this.querySelectorAll('.custom-carousel__slide');
    this.prevButtons = this.querySelectorAll('.slider-button--prev');
    this.nextButtons = this.querySelectorAll('.slider-button--next');
    this.autoPlayButton = this.querySelector('.slideshow__autoplay');
    this.counterCurrent = this.querySelectorAll('.slider-counter--current');
    this.counterTotal = this.querySelectorAll('.slider-counter--total');
    this.sliderControlLinks = this.querySelectorAll('.slider-counter__link');
    
    if (!this.slider || this.slides.length === 0) return;
    
    // Set up properties from data attributes
    this.autoPlay = this.slider.dataset.autoplay === 'true';
    this.autoPlaySpeed = parseInt(this.slider.dataset.speed) * 1000;
    
    this.currentSlide = 0;
    this.totalSlides = this.slides.length;
    this.animating = false;
    this.autoPlayInterval = null;
    
    this.initializeCarousel();
  }
  
  /**
   * Initialize the carousel functionality
   */
  initializeCarousel() {
    // Set initial state
    this.updateSlideVisibility();
    this.updateCounter();
    
    // Set up event listeners
    this.setupEventListeners();
    
    // Start autoplay if enabled
    if (this.autoPlay) {
      this.startAutoPlay();
    }
  }
  
  /**
   * Set up all event listeners for carousel controls
   */
  setupEventListeners() {
    // Previous button click
    this.prevButtons.forEach(button => {
      button.addEventListener('click', this.goToPrevSlide.bind(this));
    });
    
    // Next button click
    this.nextButtons.forEach(button => {
      button.addEventListener('click', this.goToNextSlide.bind(this));
    });
    
    // Autoplay button click
    if (this.autoPlayButton) {
      this.autoPlayButton.addEventListener('click', this.toggleAutoPlay.bind(this));
    }
    
    // Slider control links (dots/numbers)
    this.sliderControlLinks.forEach((link, index) => {
      link.addEventListener('click', () => this.goToSlide(index));
    });
    
    // Pause autoplay on hover
    this.addEventListener('mouseenter', this.pauseAutoPlay.bind(this));
    this.addEventListener('mouseleave', this.resumeAutoPlay.bind(this));
    
    // Keyboard navigation
    this.slider.setAttribute('tabindex', '0');
    this.slider.addEventListener('keydown', this.handleKeyboardNavigation.bind(this));
  }
  
  /**
   * Go to the previous slide
   */
  goToPrevSlide() {
    if (this.animating) return;
    
    this.currentSlide = (this.currentSlide - 1 + this.totalSlides) % this.totalSlides;
    this.updateSlideVisibility();
    this.updateCounter();
    this.updateSliderControlLinks();
    this.resetAutoPlay();
  }
  
  /**
   * Go to the next slide
   */
  goToNextSlide() {
    if (this.animating) return;
    
    this.currentSlide = (this.currentSlide + 1) % this.totalSlides;
    this.updateSlideVisibility();
    this.updateCounter();
    this.updateSliderControlLinks();
    this.resetAutoPlay();
  }
  
  /**
   * Go to a specific slide by index
   */
  goToSlide(index) {
    if (this.animating || this.currentSlide === index) return;
    
    this.currentSlide = index;
    this.updateSlideVisibility();
    this.updateCounter();
    this.updateSliderControlLinks();
    this.resetAutoPlay();
  }
  
  /**
   * Update the visibility of slides based on current slide index
   */
  updateSlideVisibility() {
    this.animating = true;
    
    // Hide all slides
    this.slides.forEach(slide => {
      slide.setAttribute('aria-hidden', 'true');
      slide.classList.remove('is-active');
    });
    
    // Show current slide
    const currentSlideElement = this.slides[this.currentSlide];
    currentSlideElement.setAttribute('aria-hidden', 'false');
    currentSlideElement.classList.add('is-active');
    
    // Use setTimeout to prevent rapid clicking
    setTimeout(() => {
      this.animating = false;
    }, 500);
  }
  
  /**
   * Update the counter display
   */
  updateCounter() {
    this.counterCurrent.forEach(counter => {
      counter.textContent = this.currentSlide + 1;
    });
  }
  
  /**
   * Update the active state of slider control links
   */
  updateSliderControlLinks() {
    this.sliderControlLinks.forEach((link, index) => {
      if (index === this.currentSlide) {
        link.classList.add('slider-counter__link--active');
        link.setAttribute('aria-current', 'true');
      } else {
        link.classList.remove('slider-counter__link--active');
        link.setAttribute('aria-current', 'false');
      }
    });
  }
  
  /**
   * Start the autoplay functionality
   */
  startAutoPlay() {
    if (this.autoPlayInterval) return;
    
    this.autoPlayInterval = setInterval(() => {
      this.goToNextSlide();
    }, this.autoPlaySpeed);
    
    if (this.autoPlayButton) {
      this.autoPlayButton.classList.remove('slideshow__autoplay--paused');
    }
  }
  
  /**
   * Stop the autoplay functionality
   */
  stopAutoPlay() {
    if (!this.autoPlayInterval) return;
    
    clearInterval(this.autoPlayInterval);
    this.autoPlayInterval = null;
    
    if (this.autoPlayButton) {
      this.autoPlayButton.classList.add('slideshow__autoplay--paused');
    }
  }
  
  /**
   * Toggle autoplay on/off
   */
  toggleAutoPlay() {
    if (this.autoPlayInterval) {
      this.stopAutoPlay();
    } else {
      this.startAutoPlay();
    }
  }
  
  /**
   * Pause autoplay (used for hover state)
   */
  pauseAutoPlay() {
    if (this.autoPlay && this.autoPlayInterval) {
      this.stopAutoPlay();
      this.shouldResume = true;
    }
  }
  
  /**
   * Resume autoplay if it was paused
   */
  resumeAutoPlay() {
    if (this.autoPlay && this.shouldResume) {
      this.startAutoPlay();
      this.shouldResume = false;
    }
  }
  
  /**
   * Reset autoplay timer
   */
  resetAutoPlay() {
    if (this.autoPlay && this.autoPlayInterval) {
      this.stopAutoPlay();
      this.startAutoPlay();
    }
  }
  
  /**
   * Handle keyboard navigation
   */
  handleKeyboardNavigation(event) {
    if (event.key === 'ArrowLeft') {
      this.goToPrevSlide();
    } else if (event.key === 'ArrowRight') {
      this.goToNextSlide();
    }
  }
}

// Register the custom element
customElements.define('slideshow-component', CustomCarousel);